import { ComponentProps, forwardRef, ReactNode, useId } from "react";
import {
  FieldValues,
  Path,
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";
import { X } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { Label } from "./label";

const Input = forwardRef<HTMLInputElement, ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        data-slot="input"
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base text-foreground shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export interface BaseInputProps extends ComponentProps<typeof Input> {
  label?: string;
  helperText?: string;
  errorMessage?: string;
  showHelpOnError?: boolean;
  inputClassName?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  inputWrapperClassName?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  clearable?: boolean;
  showClearButton?: boolean;
  handleClear?: () => void;
  topElement?: ReactNode;
}

const BaseInput = ({
  className,
  label,
  helperText,
  errorMessage,
  showHelpOnError,
  inputClassName,
  leftIcon,
  rightIcon,
  inputWrapperClassName,
  inputRef,
  showClearButton = false,
  handleClear,
  topElement,
  ...props
}: BaseInputProps) => {
  const id = useId();

  return (
    <div className={cn("flex flex-col gap-2 grow shrink-0", className)}>
      {(topElement || label) && (
        <div className="flex items-center gap-2">
          {label && (
            <Label htmlFor={id} className="text-sm font-medium leading-none">
              {label}
            </Label>
          )}
          {topElement}
        </div>
      )}

      <div className={cn("relative", inputWrapperClassName)}>
        {leftIcon}
        <div className="flex w-full relative">
          <Input
            {...props}
            id={id}
            className={cn(showClearButton && "pr-9", inputClassName)}
            ref={inputRef}
          />
          {showClearButton && (
            <Button
              variant="ghost"
              onClick={handleClear}
              className="absolute right-1 top-1/2 -translate-y-1/2"
              aria-label="Clear input"
            >
              <X className="h-3.5 w-3.5 text-neutral-500" />
            </Button>
          )}
        </div>
        {rightIcon}
      </div>
      {helperText && (errorMessage || showHelpOnError) && (
        <span className="text-sm text-neutral-500">{helperText}</span>
      )}
      {!!errorMessage && (
        <span className="text-xs text-destructive whitespace-pre-line">{errorMessage}</span>
      )}
    </div>
  );
};

export interface ControlledInputProps<T extends FieldValues = FieldValues>
  extends Omit<BaseInputProps, "name" | "defaultValue">, UseControllerProps<T> {
  name: Path<T>;
}

const ControlledInput = <T extends FieldValues = FieldValues>({
  name,
  defaultValue,
  rules,
  shouldUnregister,
  clearable = false,
  ...props
}: ControlledInputProps<T>) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    control,
    name,
    defaultValue,
    rules: rules as any,
    shouldUnregister,
  });

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    field.onChange(e);
    props.onChange?.(e);
  };

  const handleClear = () => {
    field.onChange("");
    if (props.onChange) {
      props.onChange({ target: { value: "" } } as any);
    }
  };

  const showClearButton = clearable && field.value && String(field.value).length > 0;

  return (
    <BaseInput
      {...field}
      {...props}
      onChange={handleChange}
      value={props.type === "file" ? undefined : field.value}
      aria-invalid={invalid}
      errorMessage={error?.message}
      showClearButton={showClearButton}
      handleClear={handleClear}
    />
  );
};

export { Input, ControlledInput, BaseInput };
